/**
 * Client half of the host policy (HOST_POLICY_SPEC.md §6 FE-B, owner decision 2026-09-01):
 * on the `.com` app hosts MachineID is INVITE-ONLY — there is no public company self-registration,
 * only an in-app "Book a Demo".
 *
 * UX-ONLY GATE. The REAL boundary is server-side and fail-closed:
 *   - Customers refuses `register` / `register/{id}` / `confirm-register` off the `.cc` surface
 *     (403 `REGISTRATION_NOT_AVAILABLE_ON_HOST`), reading the BROWSER's `Host` header;
 *   - Account's own registration endpoints are internal-only (`InternalEndpointMiddleware`).
 * Nothing here may ever be treated as the security boundary — it exists so a `.com` visitor sees
 * the right page instead of a form that would 403.
 *
 * Same shape as `customer/midas/components/v3/ccSurface.ts`: exact hostname match, fail-closed to
 * `false` (i.e. "treat as `.cc`", which is the surface that keeps every existing affordance) if
 * `window` is missing or anything throws.
 */

/** The two `.com` app hostnames bound to this site. Exact match — never a suffix/`includes` test. */
export const COM_APP_HOSTS = ["app.machine-id.com", "midas.machine-id.com"];

/**
 * Which surface a request belongs to. `cc` = the dev/test surface, `com` = the invite-only surface.
 *
 * Two things can answer this and they can DISAGREE: the client list above (a hard-coded constant)
 * and the SERVER's `HostPolicy:ComHosts` (deployed config). When the server has refused something
 * as `.com` — a 403 `REGISTRATION_NOT_AVAILABLE_ON_HOST` — the server's answer is the correct one
 * even on a hostname `COM_APP_HOSTS` has never heard of, so callers pass the refused surface
 * explicitly rather than re-deriving it from the hostname.
 */
export type HostSurface = "cc" | "com";

/** Landing origin per surface — the ONLY place the in-app lead POST may be sent. */
const COM_LANDING_ORIGIN = "https://machine-id.com";
const CC_LANDING_ORIGIN = "https://machine-id.cc";

/**
 * True when the SPA is being served from one of the `.com` app hostnames.
 *
 * EXACT `OrdinalIgnoreCase`-equivalent match (hostnames from `window.location` are already
 * lower-cased by the browser). A suffix or `includes` test would admit
 * `evil-app.machine-id.com` / `app.machine-id.com.evil`, and this decides which PAGE a route
 * renders, so it stays exact.
 */
export const isComAppHost = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      COM_APP_HOSTS.includes(window.location.hostname)
    );
  } catch {
    return false;
  }
};

/**
 * The absolute landing origin to post a lead to, for the surface we are currently on.
 *
 * WHY ABSOLUTE (HOST_POLICY_SPEC §1.7): the LeadCapture app is mounted at `/leadcapture` under the
 * two LANDING sites ONLY — it is NOT mounted under the app site. A relative
 * `POST /leadcapture/v1/leads` from an app host would hit the SPA catch-all and come back as
 * `index.html` with HTTP 200: a FALSE SUCCESS in which the lead is silently discarded. So the
 * request must name the landing origin explicitly, and the caller must additionally verify that the
 * body really is JSON `{ ok: true }`.
 *
 * Same TLD as the app host, so `.cc` never posts into the `.com` funnel (and LeadCapture derives
 * its `SourceSite` from the Host it receives).
 */
export const landingOriginForCurrentHost = (): string =>
  landingOriginForSurface(isComAppHost() ? "com" : "cc");

/**
 * The absolute landing origin for an EXPLICITLY KNOWN surface.
 *
 * THE RULE (Codex C-2): a SERVER refusal beats the client classification. `isComAppHost()` reads a
 * hard-coded list; the server reads deployed config. On a `.com` app host that has been added to
 * `HostPolicy:ComHosts` but not yet to `COM_APP_HOSTS`, `isComAppHost()` is false, so
 * `landingOriginForCurrentHost()` would send the lead to `https://machine-id.cc/leadcapture/...`
 * even though the visitor is on a `.com` app host — the lead would land in the wrong funnel (and
 * LeadCapture derives `SourceSite` from the Host it receives, so it would be mislabelled too).
 * Callers that HAVE a server refusal pass its surface here; callers that do not keep using
 * `landingOriginForCurrentHost()`, which stays the fallback.
 */
export const landingOriginForSurface = (surface: HostSurface): string =>
  surface === "com" ? COM_LANDING_ORIGIN : CC_LANDING_ORIGIN;
